import React, { useState, useEffect, useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ChevronDown, Clock, LogOut, HelpCircle, ListFilter, MessageSquare, PlusCircle, Moon, Sun, Palette, ShieldCheck, UserPlus } from 'lucide-react';
import { useManageAccessAdmin } from '../hooks/useManageAccessAdmin';
import './Navbar.css';

// Pull initials ("Roney Varghese" -> "RV") for the placeholder avatar.
const getInitials = (name) =>
  (name || 'U').split(/\s+/).map(n => n[0]).filter(Boolean).join('').slice(0, 2).toUpperCase();

const DARK_THEMES = [
  { id: 'vscode',  label: 'VS Code',  bg: '#252526', border: '#3e3e42', accent: '#569cd6' },
  { id: 'dracula', label: 'Dracula',  bg: '#21222c', border: '#44475a', accent: '#bd93f9' },
  { id: 'carbon',  label: 'Carbon',   bg: '#262626', border: '#525252', accent: '#4589ff' },
];


const Navbar = ({ darkMode, toggleDarkMode, darkTheme, setDarkTheme }) => {
  const { isAuthenticated, logout, user, getAccessTokenSilently } = useAuth0();
  const navigate = useNavigate();
  const location = useLocation();
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [isHelpOpen, setIsHelpOpen] = useState(false);
  const [isAppearanceOpen, setIsAppearanceOpen] = useState(false);
  const [isSeatingOptionsOpen, setIsSeatingOptionsOpen] = useState(false);
  const [pendingRequestsCount, setPendingRequestsCount] = useState(0);
  const [unreadJoinersExitsCount, setUnreadJoinersExitsCount] = useState(0);
  const [isSeatingAdmin, setIsSeatingAdmin] = useState(false);
  const [activeSeatingOffice, setActiveSeatingOffice] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem('pinnacle-active-seating-office') || 'null');
      if (saved?.id && saved?.name) return saved;
    } catch {}
    return { id: 'pasadena', name: 'Pasadena' };
  });
  const { isAdmin: canManageAppAccess } = useManageAccessAdmin();
  // Real M365 profile photo. Auth0's `user.picture` defaults to a Gravatar
  // identicon (a colored blob) when no custom image is set — confusing for
  // users since their actual corporate photo is available in Microsoft Graph.
  // We fetch it here and prefer it over Auth0's value; fall back to initials.
  const [graphPhoto, setGraphPhoto] = useState(null);

  useEffect(() => {
    if (!isAuthenticated || !user?.email) return;
    let cancelled = false;
    fetch(`/api/graph/photo/${encodeURIComponent(user.email)}`)
      .then(res => (res.ok ? res.json() : null))
      .then(data => {
        if (!cancelled && data?.photo) setGraphPhoto(data.photo);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [isAuthenticated, user?.email]);

  const isSeatingChartRoute = location.pathname.startsWith('/seating-chart');

  useEffect(() => {
    if (!isSeatingChartRoute) return;

    const applyOffice = (office) => {
      if (!office?.id || !office?.name) return;
      setActiveSeatingOffice(office);
      localStorage.setItem('pinnacle-active-seating-office', JSON.stringify(office));
    };

    try {
      const saved = JSON.parse(localStorage.getItem('pinnacle-active-seating-office') || 'null');
      applyOffice(saved);
    } catch {}

    const handler = (event) => applyOffice(event.detail);
    window.addEventListener('pinnacle:seating-office-change', handler);
    return () => window.removeEventListener('pinnacle:seating-office-change', handler);
  }, [isSeatingChartRoute]);

  useEffect(() => {
    if (!isAuthenticated || !isSeatingChartRoute) {
      setIsSeatingAdmin(false);
      setIsSeatingOptionsOpen(false);
      setPendingRequestsCount(0);
      setUnreadJoinersExitsCount(0);
      return;
    }

    let cancelled = false;
    getAccessTokenSilently()
      .then(token => fetch('/api/seating/is-admin', {
        headers: { Authorization: `Bearer ${token}` },
      }))
      .then(res => (res.ok ? res.json() : { isAdmin: false }))
      .then(data => {
        if (!cancelled) setIsSeatingAdmin(data.isAdmin === true);
      })
      .catch(() => {
        if (!cancelled) setIsSeatingAdmin(false);
      });

    return () => { cancelled = true; };
  }, [isAuthenticated, isSeatingChartRoute, getAccessTokenSilently]);

  const loadOptionsBadgeCounts = useCallback(async () => {
    try {
      const token = await getAccessTokenSilently();
      const officeKey = activeSeatingOffice.id;

      const [reqRes, logRes] = await Promise.all([
        fetch(`/api/seating/${officeKey}/requests?status=pending`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`/api/graph/seating-tenant-notifications/${encodeURIComponent(officeKey)}/log`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);

      if (reqRes.ok) {
        const reqData = await reqRes.json();
        setPendingRequestsCount(Array.isArray(reqData.requests) ? reqData.requests.length : 0);
      }

      if (logRes.ok) {
        const logData = await logRes.json();
        const entries = Array.isArray(logData.entries) ? logData.entries : [];
        const lastViewed = Number(localStorage.getItem(`pinnacle-${officeKey}-joiners-exits-last-viewed`) || 0);
        const unread = entries.filter(e => new Date(e.timestamp).getTime() > lastViewed).length;
        setUnreadJoinersExitsCount(unread);
      }
    } catch {}
  }, [activeSeatingOffice.id, getAccessTokenSilently]);

  useEffect(() => {
    if (!isAuthenticated || !isSeatingChartRoute || !isSeatingAdmin) {
      setPendingRequestsCount(0);
      setUnreadJoinersExitsCount(0);
      return;
    }

    loadOptionsBadgeCounts();
    const intervalId = setInterval(() => {
      if (!document.hidden) loadOptionsBadgeCounts();
    }, 60000);
    return () => clearInterval(intervalId);
  }, [isAuthenticated, isSeatingChartRoute, isSeatingAdmin, activeSeatingOffice.id, loadOptionsBadgeCounts]);

  useEffect(() => {
    const handler = () => {
      const officeKey = activeSeatingOffice.id;
      localStorage.setItem(`pinnacle-${officeKey}-joiners-exits-last-viewed`, String(Date.now()));
      setUnreadJoinersExitsCount(0);
    };
    window.addEventListener('pinnacle:joiners-exits-viewed', handler);
    return () => window.removeEventListener('pinnacle:joiners-exits-viewed', handler);
  }, [activeSeatingOffice.id]);

  const handleLogout = () => {
    logout({ logoutParams: { returnTo: window.location.origin } });
  };

  const handleManageAppAccess = () => {
    setIsProfileOpen(false);
    navigate('/manage-app-access');
  };

  const handleFeedback = () => {
    const subject = `Platform Feedback from ${user?.name || 'User'}`;
    const body = `From: ${user?.name || 'Unknown'}\nEmail: ${user?.email || 'Unknown'}\n\nFeedback:\n`;
    window.location.href = `mailto:support@pinnacletech.com?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    setIsHelpOpen(false);
  };

  const openSeatingOptions = (view = 'employees') => {
    setIsAppearanceOpen(false);
    setIsSeatingOptionsOpen(false);
    setIsHelpOpen(false);
    setIsProfileOpen(false);
    window.dispatchEvent(new CustomEvent('pinnacle:open-seating-options', { detail: { view } }));
  };

  const handleAppRequest = () => {
    const subject = `New App Request from ${user?.name || 'User'}`;
    const body = `From: ${user?.name || 'Unknown'}\nEmail: ${user?.email || 'Unknown'}\n\nApp Request Details:\n`;
    window.location.href = `mailto:support@pinnacletech.com?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    setIsHelpOpen(false);
  };

  // Don't render navbar if not authenticated (user will be redirected to login)
  if (!isAuthenticated) {
    return null;
  }

  return (
    <nav className="navbar">
      <div className="navbar-container">
        <a href="/" className="navbar-logo">
          <img src="/pinnaclelogo.svg" alt="Pinnacle Platform" className="logo-image" />
        </a>

        <div className="navbar-actions">
          {/* Appearance Dropdown — dark mode toggle + theme picker */}
          <div className="appearance-dropdown">
            <button
              className={`dark-mode-toggle-nav ${isAppearanceOpen ? 'active' : ''}`}
              onClick={() => setIsAppearanceOpen(!isAppearanceOpen)}
              aria-label="Appearance settings"
            >
              {darkMode ? <Sun size={20} /> : <Moon size={20} />}
            </button>

            {isAppearanceOpen && (
              <>
                <div className="appearance-overlay" onClick={() => setIsAppearanceOpen(false)} />
                <div className="appearance-menu">
                  <div className="appearance-header">Appearance</div>

                  {/* Light / Dark toggle row */}
                  <div className="appearance-mode-row">
                    <span className="appearance-mode-label">
                      {darkMode ? <Moon size={14} /> : <Sun size={14} />}
                      {darkMode ? 'Dark mode' : 'Light mode'}
                    </span>
                    <button
                      className={`mode-toggle-pill ${darkMode ? 'on' : 'off'}`}
                      onClick={toggleDarkMode}
                      aria-label="Toggle dark mode"
                    >
                      <span className="mode-toggle-thumb" />
                    </button>
                  </div>

                  {/* Theme swatches — only when dark mode is active */}
                  {darkMode && (
                    <>
                      <div className="appearance-divider" />
                      <div className="theme-picker">
                        <div className="theme-picker-header">
                          <Palette size={13} />
                          <span className="theme-picker-label">Dark Theme</span>
                        </div>
                        <div className="theme-swatches">
                          {DARK_THEMES.map(theme => (
                            <button
                              key={theme.id}
                              className={`theme-swatch ${darkTheme === theme.id ? 'active' : ''}`}
                              onClick={() => { setDarkTheme(theme.id); }}
                              title={theme.label}
                              style={{
                                '--swatch-bg': theme.bg,
                                '--swatch-border': theme.border,
                                '--swatch-accent': theme.accent,
                              }}
                            >
                              <span className="swatch-preview" />
                              <span className="swatch-name">{theme.label}</span>
                              {darkTheme === theme.id && (
                                <span className="swatch-active-dot" />
                              )}
                            </button>
                          ))}
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </>
            )}
          </div>

          {isSeatingChartRoute && isSeatingAdmin && (
            <div className="seating-options-dropdown">
              <button
                className={`seating-options-button ${isSeatingOptionsOpen ? 'active' : ''}`}
                onClick={() => {
                  const nextOpen = !isSeatingOptionsOpen;
                  setIsSeatingOptionsOpen(nextOpen);
                  if (nextOpen) {
                    setIsAppearanceOpen(false);
                    setIsHelpOpen(false);
                    setIsProfileOpen(false);
                  }
                }}
                aria-label="Seating chart options"
                title="Seating chart options"
              >
                <ListFilter size={17} />
                <span>Options</span>
                {(pendingRequestsCount + unreadJoinersExitsCount) > 0 && (
                  <span className="options-badge">
                    {pendingRequestsCount + unreadJoinersExitsCount > 99 ? '99+' : pendingRequestsCount + unreadJoinersExitsCount}
                  </span>
                )}
                <ChevronDown size={14} className={`chevron ${isSeatingOptionsOpen ? 'rotated' : ''}`} />
              </button>

              {isSeatingOptionsOpen && (
                <>
                  <div className="seating-options-overlay" onClick={() => setIsSeatingOptionsOpen(false)} />
                  <div className="seating-options-menu">
                    <button className="seating-options-menu-item" onClick={() => openSeatingOptions('employees')}>
                      <ListFilter size={15} />
                      List of employees
                    </button>
                    <button className="seating-options-menu-item" onClick={() => openSeatingOptions('requests')}>
                      <Clock size={15} />
                      Seat change requests
                      {pendingRequestsCount > 0 && (
                        <span className="options-menu-badge">{pendingRequestsCount > 99 ? '99+' : pendingRequestsCount}</span>
                      )}
                    </button>
                    <button className="seating-options-menu-item" onClick={() => openSeatingOptions('joiners')}>
                      <UserPlus size={15} />
                      Joiners &amp; Exits
                      {unreadJoinersExitsCount > 0 && (
                        <span className="options-menu-badge">{unreadJoinersExitsCount > 99 ? '99+' : unreadJoinersExitsCount}</span>
                      )}
                    </button>
                  </div>
                </>
              )}
            </div>
          )}

          {/* Help Dropdown */}
          <div className="help-dropdown">
            <button
              className="help-button"
              onClick={() => setIsHelpOpen(!isHelpOpen)}
            >
              <HelpCircle size={20} />
            </button>

            {isHelpOpen && (
              <>
                <div className="help-overlay" onClick={() => setIsHelpOpen(false)} />
                <div className="help-menu">
                  <button className="help-menu-item" onClick={handleFeedback}>
                    <MessageSquare size={16} />
                    Give Feedback
                  </button>
                  <button className="help-menu-item" onClick={handleAppRequest}>
                    <PlusCircle size={16} />
                    Request an App
                  </button>
                </div>
              </>
            )}
          </div>

          <div className="profile-dropdown">
            <button
              className="profile-trigger"
              onClick={() => setIsProfileOpen(!isProfileOpen)}
            >
              {graphPhoto ? (
                <img src={graphPhoto} alt={user?.name} className="profile-avatar" />
              ) : (
                <span className="profile-avatar-initials" aria-label={user?.name}>
                  {getInitials(user?.name)}
                </span>
              )}
              <span className="profile-name">{user?.name || 'User'}</span>
              <ChevronDown size={16} className={`chevron ${isProfileOpen ? 'rotated' : ''}`} />
            </button>

            {isProfileOpen && (
              <>
                <div className="profile-overlay" onClick={() => setIsProfileOpen(false)} />
                <div className="profile-menu">
                  <div className="profile-header">
                    {graphPhoto ? (
                      <img src={graphPhoto} alt={user?.name} className="profile-menu-avatar" />
                    ) : (
                      <div className="profile-menu-avatar-placeholder" aria-label={user?.name}>
                        {getInitials(user?.name)}
                      </div>
                    )}
                    <div className="profile-info">
                      <span className="profile-menu-name">{user?.name}</span>
                      <span className="profile-menu-email">{user?.email}</span>
                    </div>
                  </div>
                  {canManageAppAccess && (
                    <button className="profile-menu-item" onClick={handleManageAppAccess}>
                      <ShieldCheck size={16} />
                      Manage App Access
                    </button>
                  )}
                  <div className="profile-divider" />
                  <button className="profile-menu-item logout" onClick={handleLogout}>
                    <LogOut size={16} />
                    Sign Out
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
